import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type UpdateTourStatusInput, inquiryNotesApi, tourRequestsApi } from '@/services';

export const tourRequestsKeys = {
  all: ['admin', 'tour-requests'] as const,
  list: () => [...tourRequestsKeys.all, 'list'] as const,
  detail: (id: string) => [...tourRequestsKeys.all, 'detail', id] as const,
};

export function useTourRequestsListQuery() {
  return useQuery({
    queryKey: tourRequestsKeys.list(),
    queryFn: () => tourRequestsApi.getAll(),
  });
}

export function useTourRequestDetailQuery(id: string) {
  return useQuery({
    queryKey: tourRequestsKeys.detail(id),
    queryFn: () => tourRequestsApi.getById(id),
    enabled: Boolean(id),
  });
}

/** Cached hard: the staff list changes far less often than a tour queue is worked through. */
export function useTourHostsQuery(enabled = true) {
  return useQuery({
    queryKey: [...tourRequestsKeys.all, 'hosts'],
    queryFn: () => tourRequestsApi.hosts(),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function useUpdateTourRequestStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateTourStatusInput) => tourRequestsApi.updateStatus(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: tourRequestsKeys.list() });
      queryClient.invalidateQueries({ queryKey: tourRequestsKeys.detail(id) });
    },
  });
}

export function useDeleteTourRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tourRequestsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tourRequestsKeys.all });
    },
  });
}

export function useAddTourRequestNoteMutation(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      inquiryNotesApi.create({ entityType: 'tour_request', entityId: requestId, content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tourRequestsKeys.detail(requestId) });
    },
  });
}

export function useDeleteTourRequestNoteMutation(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => inquiryNotesApi.delete(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tourRequestsKeys.detail(requestId) });
    },
  });
}
